import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import {
    catchError, filter, first, map, switchMap, withLatestFrom,
} from 'rxjs/operators';

import { generateMarketLanguageFromPath, getUrlWithoutCountryLangFromRoute } from '../use-cases/market-use-cases';

import {
    LoadMarketLanguagesError,
    LoadMarketLanguagesSuccess,
    InternalNavigation, LoadPagesAndHomesMarketLanguages, LoadHomeMarketTranslatedLanguages, LoadMarketLanguages,
    LoadCategoryAndProductMarketLanguages,
    InitMarketLanguages,
} from './market.actions';
import { getUrlTranslations } from './market.selectors';
import { MarketLanguage, UrlTranslations } from './market.state';

import { Category } from '@core/domain/models/category.models';
import { HomeConfig } from '@core/domain/state/core.state';
import { ConfigService } from '@core/infrastructure/config/config.service';
import { getHomeConfig, getStore } from '@core/infrastructure/state/core.selectors';
import { CategoriesSelectors } from '@features/category';
import { routerPublicModels, routerPublicQueries, routerPublicCommands } from '@frontend/libs/duttirouter';
import { StoreDetail, StoreLanguage } from '@mdfrontw/core';

@Injectable()
export class MarketEffects {
    public baseUrl: string;

    private readonly domain = this.configService.getConfigUrl<string>('baseUrl');

    private readonly getStore$ = this.store.select(getStore);

    private readonly getMergedRoute$ = this.store.select(routerPublicQueries.getMergedRoute);

    private readonly getHomeConfig$ = this.store.select(getHomeConfig).pipe(
        filter((homeConfigs) => {
            const urls = homeConfigs.map(config => config.locationPath);
            return urls.some(url => url.length > 0);
        }),
    );

    private readonly getCategoriesNavigation$ = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    private readonly getUrlTranslations$ = this.store.select(getUrlTranslations);

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly configService: ConfigService,
    ) { }

    public loadPagesAndHomesMarketLanguages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                InitMarketLanguages,
                InternalNavigation,
                routerPublicCommands.SetLocationHistory,
                LoadPagesAndHomesMarketLanguages,
            ),
            withLatestFrom(
                this.getStore$,
                this.getMergedRoute$.pipe(
                    filter(url => !!url),
                ),
                this.getHomeConfig$,
            ),
            map(([_, store, route, homeConfigs]: [any, StoreDetail, routerPublicModels.MergedRoute, HomeConfig[]]) => {
                const urlTree = route.url.split('?');
                const urlWithoutParams = urlTree.shift() || '';
                const mergedCopyRoute: routerPublicModels.MergedRoute = { ...route, url: urlWithoutParams };
                const pathWithoutCountryLang: string = getUrlWithoutCountryLangFromRoute(mergedCopyRoute);
                const homeTranslatedPage: HomeConfig = homeConfigs.find((config) =>
                    config.locationPath === mergedCopyRoute.url);

                if (pathWithoutCountryLang.length > 1 && homeTranslatedPage) {
                    return LoadHomeMarketTranslatedLanguages({
                        storeDetail: store,
                        route: mergedCopyRoute,
                        categoryID: homeTranslatedPage.categoryId,
                    });
                }
                const languages: MarketLanguage[] = store.supportedLanguages.map((language: StoreLanguage) => {
                    return generateMarketLanguageFromPath(
                        store,
                        language,
                        pathWithoutCountryLang,
                        this.domain,
                    );
                });

                return LoadMarketLanguagesSuccess({
                    marketLanguages: languages,
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => of(LoadMarketLanguagesError())),
        );
    });

    // TODO ECOMDUTI-21025 [TechDebt][Comercial] - Exceso de complejidad, las categorias que tenemos en eCommerceContext pueden ser facilmente
    // extendidas para tener categoryUrl pues vienen en la respuesta del API y con eso no necesitamos tanta llamadas en paralelo
    public loadHomeMarketTranslatedLanguages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadHomeMarketTranslatedLanguages),
            switchMap(() => {
                return combineLatest([
                    this.getStore$,
                    this.getCategoriesNavigation$,
                    this.getMergedRoute$,
                ]).pipe(first());
            }),
            map(([store, cats, route]: [StoreDetail, any, routerPublicModels.MergedRoute]) => {
                const category = cats.find((cat: Category) => cat.locationPath === route.url);

                const languages: MarketLanguage[] = category?.categoryUrlTranslations?.map((translation) => {
                    const language = store.supportedLanguages.find((lang) => lang.id === translation.id.toString());
                    const categoryPath = translation.name;
                    return generateMarketLanguageFromPath(
                        store,
                        language,
                        categoryPath,
                        this.domain,
                    );
                });

                return languages;
            }),
            filter(languages => languages.length > 0),
            map(languages => {
                return LoadMarketLanguagesSuccess({
                    marketLanguages: languages,
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => of(LoadMarketLanguagesError())),
        );
    });

    public loadCategoryTranslatedMarketLanguages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadMarketLanguages),
            concatLatestFrom(() => [
                this.getStore$,
            ]),
            map(([{ languagesPath }, store]) => {
                const languages = languagesPath.map(({ language, url }) => {
                    return generateMarketLanguageFromPath(
                        store,
                        language,
                        url,
                        this.domain,
                    );
                });
                return languages;
            }),
            filter(languages => languages.length > 0),
            map((languages) => {
                return LoadMarketLanguagesSuccess({
                    marketLanguages: languages,
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => of(LoadMarketLanguagesError())),
        );
    });

    public loadCategoryAndProductTranslatedMarketLanguages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryAndProductMarketLanguages),
            switchMap(() => {
                return combineLatest([
                    this.getStore$,
                    this.getUrlTranslations$,
                    this.getMergedRoute$,
                ]).pipe(first());
            }),
            map(([store, urlTranslations, mergedRoute]:
            [StoreDetail, UrlTranslations[], routerPublicModels.MergedRoute]) => {
                const languages: MarketLanguage[] = urlTranslations?.reduce((acc, translation) => {
                    const language = store.supportedLanguages.find((lang) => lang.id === translation.id.toString());
                    const categoryPath = translation.name;
                    const categoryUrl: string = mergedRoute.queryParams.g ? `${categoryPath}?g=${mergedRoute.queryParams.g}` : categoryPath;

                    if (language && !acc.some(lang => lang.id === language.id)) {
                        const marketLanguage = generateMarketLanguageFromPath(
                            store,
                            language,
                            categoryUrl,
                            this.domain,
                        );
                        acc.push(marketLanguage);
                    }
                    return acc;
                }, []);
                return languages;
            }),
            filter(languages => languages.length > 0),
            map((languages) => {
                return LoadMarketLanguagesSuccess({
                    marketLanguages: languages,
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => of(LoadMarketLanguagesError())),
        );
    });
}
